import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ShieldIcon from '@mui/icons-material/Shield';
import LockIcon from '@mui/icons-material/Lock';
import BugReportIcon from '@mui/icons-material/BugReport';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import { securityService, SecurityDashboard as SecurityDashboardType, SecurityEvent, ThreatAlert } from '../api/services/security';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SecurityDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<SecurityDashboardType | null>(null);
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [threats, setThreats] = useState<ThreatAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthError, setIsAuthError] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState<SecurityEvent | null>(null);
  const [selectedThreat, setSelectedThreat] = useState<ThreatAlert | null>(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [blockReason, setBlockReason] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsAuthError(false);

      const [dashboardData, eventsData, threatsData] = await Promise.all([
        securityService.getDashboard(30),
        securityService.getEvents({ limit: 50 }),
        securityService.getThreats({ limit: 50 }),
      ]);

      setDashboard(dashboardData);
      setEvents(eventsData.events);
      setThreats(threatsData.threats);
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        setIsAuthError(true);
        setError('Authentication required to access Security Dashboard');
      } else {
        setError(err.response?.data?.detail || 'Failed to load security data');
      }
      console.error('Error fetching security data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveEvent = async () => {
    if (!selectedEvent) return;

    try {
      await securityService.resolveEvent(selectedEvent.event_id, {
        resolution_notes: resolutionNotes,
      });
      setResolveDialogOpen(false);
      setResolutionNotes('');
      setSelectedEvent(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to resolve event');
    }
  };

  const handleBlockThreat = async () => {
    if (!selectedThreat) return;

    try {
      await securityService.blockThreat(selectedThreat.alert_id, {
        block_reason: blockReason,
      });
      setBlockDialogOpen(false);
      setBlockReason('');
      setSelectedThreat(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to block threat');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return <ErrorIcon />;
      case 'high':
        return <WarningIcon />;
      case 'medium':
        return <SecurityIcon />;
      case 'low':
        return <CheckCircleIcon />;
      default:
        return <SecurityIcon />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    if (isAuthError) {
      return (
        <Box p={3}>
          <Card sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <LockIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Access Restricted
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                The Security Dashboard requires administrator privileges.
                This feature will be available once your account has been granted the appropriate permissions.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </Box>
      );
    }
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!dashboard) return null;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <ShieldIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Security Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor security events, threats, and compliance status
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {dashboard.dashboard.security_events.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Security Events
                  </Typography>
                  <Chip
                    label={`${dashboard.dashboard.security_events.unresolved} Unresolved`}
                    size="small"
                    color="warning"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <BugReportIcon sx={{ fontSize: 40, color: 'error.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {dashboard.dashboard.threat_detection.threats_detected}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Threats Detected
                  </Typography>
                  <Chip
                    label={`${dashboard.dashboard.threat_detection.threats_blocked} Blocked`}
                    size="small"
                    color="success"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <LockIcon sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {dashboard.dashboard.authentication.success_rate.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Auth Success Rate
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dashboard.dashboard.authentication.total_attempts} attempts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {dashboard.dashboard.compliance_status.score}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compliance Score
                  </Typography>
                  <Chip
                    label={dashboard.dashboard.compliance_status.status}
                    size="small"
                    color={dashboard.dashboard.compliance_status.score >= 90 ? 'success' : 'warning'}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Security Events" />
          <Tab label="Threat Alerts" />
          <Tab label="Authentication Logs" />
          <Tab label="Anomalies" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Event ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Source IP</TableCell>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {event.event_id.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>{event.event_type}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getSeverityIcon(event.severity)}
                        label={event.severity}
                        size="small"
                        color={getSeverityColor(event.severity) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {event.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {event.source_ip || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(event.timestamp).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={event.resolved ? 'Resolved' : 'Open'}
                        size="small"
                        color={event.resolved ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      {!event.resolved && (
                        <Tooltip title="Resolve Event">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedEvent(event);
                              setResolveDialogOpen(true);
                            }}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Alert ID</TableCell>
                  <TableCell>Threat Type</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Source IP</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Detected At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {threats.map((threat) => (
                  <TableRow key={threat.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {threat.alert_id.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>{threat.threat_type}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getSeverityIcon(threat.severity)}
                        label={threat.severity}
                        size="small"
                        color={getSeverityColor(threat.severity) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={threat.confidence_score * 100}
                          sx={{ width: 60 }}
                        />
                        <Typography variant="body2">
                          {(threat.confidence_score * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {threat.source_ip}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {threat.target_resource}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(threat.detected_at).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={threat.blocked ? 'Blocked' : threat.status}
                        size="small"
                        color={threat.blocked ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      {!threat.blocked && (
                        <Tooltip title="Block Threat">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedThreat(threat);
                              setBlockDialogOpen(true);
                            }}
                          >
                            <BlockIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography>Authentication Logs content coming...</Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography>Anomalies content coming...</Typography>
        </TabPanel>
      </Card>

      {/* Resolve Event Dialog */}
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Security Event</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Resolution Notes"
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResolveEvent} variant="contained" color="primary">
            Resolve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block Threat Dialog */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Block Threat</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Block Reason"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBlockThreat} variant="contained" color="error">
            Block
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityDashboard;
